import { useEffect, useMemo, useState, useCallback } from "react";
import { Bell, EnvelopeSimple, DeviceMobile, Clock, Buildings, Shield, HardDrives, FloppyDisk, ArrowClockwise, CheckCircle, Play, CalendarCheck, Envelope, Copy, Trash, Plus, UserPlus, Gauge, GasPump, CircleNotch, FileText } from "@phosphor-icons/react";
import axios from "../../utils/axiosInstance";

const API = window.location.origin;

// ============== SECTION HEADER ==============
function SectionCard({ icon: Icon, title, subtitle, children, right, defaultOpen = false }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="bg-white rounded-xl border border-slate-200 shadow-sm">
      <div className="flex items-center justify-between gap-2 px-4 sm:px-5 py-3 border-b border-slate-100">
        <button
          onClick={() => setOpen((v) => !v)}
          className="flex items-center gap-2 flex-1 text-left"
          data-testid={`section-toggle-${title}`}
        >
          <div className="w-8 h-8 rounded-md bg-blue-50 text-blue-700 flex items-center justify-center">
            <Icon className="w-4 h-4" weight="fill" />
          </div>
          <div className="flex-1">
            <div className="text-sm font-semibold text-slate-800">{title}</div>
            {subtitle && <div className="text-xs text-slate-500">{subtitle}</div>}
          </div>
          <span className="text-slate-400 text-xs">{open ? "▾" : "▸"}</span>
        </button>
        {right}
      </div>
      {open && <div className="p-4 sm:p-5">{children}</div>}
    </div>
  );
}

// ============== EVENT LABELS ==============
const EVENTS = [
  ["salary_published",   "Wysłanie wypłaty pracownikowi",   "Kwota przelewu, okres — dla pracownika"],
  ["urlop_new_request",  "Nowy wniosek o urlop",             "Wysyłane do administratora"],
  ["urlop_decision",     "Decyzja o urlopie",                "Zatwierdzenie / odrzucenie — dla pracownika"],
  ["chat_message",       "Nowa wiadomość na czacie",         "Powiadomienia grupowe (bez nadawcy)"],
  ["daily_terminy",      "Terminy pojazdów",                 "Codzienne przypomnienie o wygasających dokumentach"],
  ["birthdays",          "Zbliżające się urodziny",          "Pracownicy z urodzinami za 3 dni"],
  ["saturday_holiday",   "Święta w sobotę",                  "Miesięczne przypomnienie o odbiorze dnia wolnego"],
  ["weekly_backup",      "Cotygodniowa kopia zapasowa",      "ZIP z bazą danych na wskazany adres email"],
  ["monthly_mileage",    "Miesięczne wpisanie przebiegu",    "Nudge dla kurierów pierwszego dnia miesiąca"],
  ["dyzur",              "Dyżur sobotni",                    "Powiadomienie o zbliżającym się dyżurze"],
  ["mail_rule_applied",  "Poczta — zastosowana reguła",      "Push gdy sortowanie IMAP przenosi wiadomość"],
];

const DAYS = [
  ["mon", "Poniedziałek"], ["tue", "Wtorek"], ["wed", "Środa"],
  ["thu", "Czwartek"], ["fri", "Piątek"], ["sat", "Sobota"], ["sun", "Niedziela"],
];


export default function SettingsTab() {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [savedAt, setSavedAt] = useState(null);
  const [error, setError] = useState(null);
  const [backupRunning, setBackupRunning] = useState(false);
  const [backupMsg, setBackupMsg] = useState(null);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const { data } = await axios.get(`${API}/api/settings`);
      setSettings(data);
      setDirty(false);
    } catch (e) {
      setError(e?.response?.data?.detail || "Nie udało się pobrać ustawień");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const setField = (section, key, value) => {
    setSettings((prev) => ({
      ...prev,
      [section]: { ...prev[section], [key]: value },
    }));
    setDirty(true);
  };

  const setChannel = (event, channel, value) => {
    setSettings((prev) => ({
      ...prev,
      channels: {
        ...prev.channels,
        [event]: { ...prev.channels[event], [channel]: value },
      },
    }));
    setDirty(true);
  };

  const save = async () => {
    setSaving(true);
    setError(null);
    try {
      const { data } = await axios.patch(`${API}/api/settings`, settings);
      setSettings(data.settings || settings);
      setDirty(false);
      setSavedAt(new Date());
      if (data.reload_error) {
        setError(`Zapisano, ale reload harmonogramu nie powiódł się: ${data.reload_error}`);
      }
    } catch (e) {
      setError(e?.response?.data?.detail || "Nie udało się zapisać");
    } finally {
      setSaving(false);
    }
  };

  const runBackup = async () => {
    setBackupRunning(true);
    setBackupMsg(null);
    try {
      const { data } = await axios.post(`${API}/api/settings/backup/run`);
      setBackupMsg({ ok: true, text: data.message || "Backup uruchomiony" });
    } catch (e) {
      setBackupMsg({ ok: false, text: e?.response?.data?.detail || "Nie udało się uruchomić" });
    } finally {
      setBackupRunning(false);
      setTimeout(() => setBackupMsg(null), 8000);
    }
  };

  const savedAgo = useMemo(() => {
    if (!savedAt) return null;
    return savedAt.toLocaleTimeString("pl-PL", { hour: "2-digit", minute: "2-digit" });
  }, [savedAt]);

  if (loading || !settings) {
    return <div className="text-center py-12 text-slate-500">Ładowanie ustawień...</div>;
  }

  return (
    <div className="max-w-[1100px] mx-auto px-3 py-4 sm:px-6 sm:py-6 space-y-4 sm:space-y-5" data-testid="settings-container">
      {/* Sticky save bar */}
      <div className="sticky top-0 z-10 -mx-3 sm:-mx-6 px-3 sm:px-6 py-3 bg-slate-50/95 backdrop-blur border-b border-slate-200 flex items-center justify-between gap-3">
        <div>
          <h2 className="text-lg sm:text-2xl font-bold text-slate-800">Ustawienia</h2>
          <p className="text-xs text-slate-500">Kanały powiadomień, harmonogramy, firma, bezpieczeństwo, backup.</p>
        </div>
        <div className="flex items-center gap-2">
          {error && <span className="text-xs text-red-600 max-w-[300px] truncate">{error}</span>}
          {!error && savedAgo && !dirty && (
            <span className="text-xs text-emerald-600 flex items-center gap-1">
              <CheckCircle className="w-3.5 h-3.5" weight="fill" /> Zapisano · {savedAgo}
            </span>
          )}
          <button
            onClick={load}
            disabled={saving}
            className="p-2 text-slate-500 hover:bg-slate-200 rounded-md"
            title="Odśwież"
          >
            <ArrowClockwise className={`w-4 h-4 ${saving ? "animate-spin" : ""}`} />
          </button>
          <button
            onClick={save}
            disabled={!dirty || saving}
            className="inline-flex items-center gap-2 px-4 py-2 bg-blue-700 hover:bg-blue-800 text-white rounded-md text-sm font-medium disabled:opacity-40 disabled:cursor-not-allowed shadow-sm"
            data-testid="settings-save-btn"
          >
            <FloppyDisk className="w-4 h-4" weight="fill" /> Zapisz
          </button>
        </div>
      </div>

      {/* Section 1 — Channels */}
      <SectionCard icon={Bell} title="Kanały powiadomień" subtitle="Wybierz jak wysyłać każdy typ powiadomienia">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs uppercase text-slate-500">
                <th className="pb-2 pr-4 font-medium">Zdarzenie</th>
                <th className="pb-2 px-2 font-medium text-center w-24">
                  <EnvelopeSimple className="w-4 h-4 inline mr-1" /> Email
                </th>
                <th className="pb-2 px-2 font-medium text-center w-24">
                  <DeviceMobile className="w-4 h-4 inline mr-1" /> Push
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {EVENTS.map(([key, label, hint]) => {
                const ch = settings.channels[key] || {};
                return (
                  <tr key={key} data-testid={`channel-row-${key}`}>
                    <td className="py-2 pr-4">
                      <div className="text-slate-800 font-medium">{label}</div>
                      <div className="text-xs text-slate-500">{hint}</div>
                    </td>
                    <td className="py-2 px-2 text-center">
                      <label className="inline-flex items-center justify-center cursor-pointer">
                        <input
                          type="checkbox"
                          checked={!!ch.email}
                          onChange={(e) => setChannel(key, "email", e.target.checked)}
                          className="w-4 h-4 accent-blue-700"
                          data-testid={`channel-${key}-email`}
                        />
                      </label>
                    </td>
                    <td className="py-2 px-2 text-center">
                      <label className="inline-flex items-center justify-center cursor-pointer">
                        <input
                          type="checkbox"
                          checked={!!ch.push}
                          onChange={(e) => setChannel(key, "push", e.target.checked)}
                          className="w-4 h-4 accent-blue-700"
                          data-testid={`channel-${key}-push`}
                        />
                      </label>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </SectionCard>

      {/* Section 2 — Schedules */}
      <SectionCard icon={Clock} title="Harmonogramy" subtitle="Kiedy uruchamiać codzienne / tygodniowe zadania">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <TimeField
            label="Terminy pojazdów — wysyłka codzienna"
            value={settings.schedules.terminy_time}
            onChange={(v) => setField("schedules", "terminy_time", v)}
            testid="schedule-terminy"
          />
          <TimeField
            label="Urodziny — wysyłka codzienna"
            value={settings.schedules.birthdays_time}
            onChange={(v) => setField("schedules", "birthdays_time", v)}
            testid="schedule-birthdays"
          />
          <div>
            <label className="text-xs font-medium text-slate-600 block mb-1">Dzień tygodnia backupu</label>
            <select
              value={settings.schedules.backup_day}
              onChange={(e) => setField("schedules", "backup_day", e.target.value)}
              className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm bg-white"
              data-testid="schedule-backup-day"
            >
              {DAYS.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
            </select>
          </div>
          <TimeField
            label="Godzina backupu"
            value={settings.schedules.backup_time}
            onChange={(v) => setField("schedules", "backup_time", v)}
            testid="schedule-backup-time"
          />
          <TimeField
            label="Święta w sobotę — wysyłka 25. dnia miesiąca"
            value={settings.schedules.holidays_time}
            onChange={(v) => setField("schedules", "holidays_time", v)}
            testid="schedule-holidays"
          />
        </div>
      </SectionCard>

      {/* Section 3 — Company */}
      <SectionCard icon={Buildings} title="Firma" subtitle="Dane używane w PDF-ach i nagłówkach">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <TextField label="Nazwa firmy" value={settings.company.name} onChange={(v) => setField("company", "name", v)} testid="company-name" />
          <TextField label="NIP" value={settings.company.nip} onChange={(v) => setField("company", "nip", v)} testid="company-nip" />
          <TextField label="Telefon" value={settings.company.phone} onChange={(v) => setField("company", "phone", v)} testid="company-phone" />
          <TextField label="Email" value={settings.company.email} onChange={(v) => setField("company", "email", v)} testid="company-email" />
          <div className="sm:col-span-2">
            <TextField label="Adres" value={settings.company.address} onChange={(v) => setField("company", "address", v)} testid="company-address" />
          </div>
        </div>
      </SectionCard>

      {/* Section 4 — Security */}
      <SectionCard icon={Shield} title="Bezpieczeństwo" subtitle="Polityka haseł i sesji">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <NumberField
            label="Wymuś zmianę hasła co (dni)"
            hint="0 = wyłączone"
            value={settings.security.password_max_age_days}
            onChange={(v) => setField("security", "password_max_age_days", v)}
            testid="security-password-age"
          />
          <NumberField
            label="Auto-wyloguj po (minuty bezczynności)"
            hint="0 = wyłączone"
            value={settings.security.idle_logout_minutes}
            onChange={(v) => setField("security", "idle_logout_minutes", v)}
            testid="security-idle"
          />
          <NumberField
            label="Blokada po N nieudanych logowaniach"
            hint="np. 5"
            value={settings.security.max_failed_logins}
            onChange={(v) => setField("security", "max_failed_logins", v)}
            testid="security-failed"
          />
        </div>
      </SectionCard>

      {/* Section 5 — Backup */}
      <SectionCard
        icon={HardDrives}
        title="Backup / Dane"
        subtitle="Kopia zapasowa i retencja logów"
        right={
          <button
            onClick={runBackup}
            disabled={backupRunning}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded-md text-xs font-medium disabled:opacity-50"
            data-testid="backup-run-now"
          >
            <Play className="w-3.5 h-3.5" weight="fill" /> Zrób backup teraz
          </button>
        }
      >
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <TextField
            label="Adres email na który wysyłać backup"
            hint="Pozostaw puste, aby użyć ADMIN_NOTIFICATION_EMAIL z środowiska"
            value={settings.backup.email_to}
            onChange={(v) => setField("backup", "email_to", v)}
            testid="backup-email-to"
          />
          <NumberField
            label="Retencja audit logu (dni)"
            hint="0 = nie usuwaj"
            value={settings.backup.audit_retention_days}
            onChange={(v) => setField("backup", "audit_retention_days", v)}
            testid="backup-retention"
          />
        </div>
        {backupMsg && (
          <div className={`mt-3 text-sm px-3 py-2 rounded-md ${backupMsg.ok ? "bg-emerald-50 text-emerald-800 border border-emerald-200" : "bg-red-50 text-red-800 border border-red-200"}`}>
            {backupMsg.text}
          </div>
        )}
      </SectionCard>

      {/* Section 6 — Dyżury */}
      <SectionCard icon={CalendarCheck} title="Dyżury sobotnie" subtitle="Kiedy przypominać pracownikom o zbliżającym się dyżurze">
        <DyzuryRemindersEditor
          value={settings.dyzury?.reminders || []}
          onChange={(arr) => setField("dyzury", "reminders", arr)}
        />
      </SectionCard>

      {/* Section 6b — Przebiegi reminder */}
      <SectionCard icon={Clock} title="Przypomnienie o wpisaniu przebiegu" subtitle="Kiedy pingnąć kurierów (i admina), żeby wpisali aktualny przebieg auta">
        <PrzebiegiReminderEditor
          value={settings.przebiegi?.reminder || { day: 1, time: "06:00", notify_admin: true }}
          onChange={(next) => setField("przebiegi", "reminder", next)}
        />
      </SectionCard>

      {/* Section 6c — Orlen auto-sync */}
      <SectionCard icon={Gauge} title="Orlen — automatyczna synchronizacja" subtitle="Pobieraj tankowania z Flota24 co N godzin i automatycznie łącz z wpisami pracowników">
        <OrlenSettingsEditor />
      </SectionCard>

      {/* Section 6d — KSeF auto-sync */}
      <SectionCard icon={FileText} title="KSeF 2.0 — faktury zakupowe" subtitle="Automatyczne pobieranie faktur zakupowych z Krajowego Systemu e-Faktur">
        <KsefSettingsEditor />
      </SectionCard>

      {/* Section 7 — Tabs visibility */}
      <SectionCard icon={Shield} title="Widoczne zakładki (Admin)" subtitle="Ukryj niepotrzebne zakładki z paska bocznego">
        <TabsVisibilityEditor
          value={settings.ui?.hidden_admin_tabs || []}
          onChange={(arr) => setField("ui", "hidden_admin_tabs", arr)}
          role="admin"
        />
      </SectionCard>

      {/* Section 8 — Invites */}
      <InvitesSection />
    </div>
  );
}

// ============== TABS VISIBILITY EDITOR ==============
const ADMIN_TABS = [
  ["dashboard-home", "Panel"],
  ["salary", "Wynagrodzenia"],
  ["pojazdy", "Pojazdy"],
  ["terminy", "Terminy"],
  ["paliwo", "Paliwo"],
  ["pozyczka", "Pożyczki"],
  ["credits", "Kredyty"],
  ["private-credits", "Prywatne kredyty"],
  ["dochod-firmy", "Dochód firmy"],
  ["ksef", "KSeF"],
  ["uslugi-sieciowe", "Usługi sieciowe"],
  ["urzadzenia", "Urządzenia"],
  ["employees", "Pracownicy"],
  ["urlopy", "Urlopy"],
  ["documents", "Dokumenty"],
  ["chat", "Czat"],
  ["kontakty", "Kontakty"],
  ["rejony", "Rejony"],
  ["przypomnienia", "Przypomnienia"],
  ["dyzury", "Dyżury"],
  ["audit", "Aktywność"],
];

function TabsVisibilityEditor({ value, onChange }) {
  const hidden = new Set(value || []);
  const toggle = (key) => {
    const next = new Set(hidden);
    if (next.has(key)) next.delete(key); else next.add(key);
    onChange([...next]);
  };
  return (
    <div>
      <div className="text-[11px] text-slate-500 mb-3">
        Odznacz zakładki, których nie używasz — nie będą widoczne w pasku bocznym.
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
        {ADMIN_TABS.map(([key, label]) => {
          const visible = !hidden.has(key);
          return (
            <label key={key} className={`flex items-center gap-2 px-3 py-2 border rounded-md cursor-pointer text-sm ${visible ? "border-blue-200 bg-blue-50/50" : "border-slate-200 bg-slate-50 text-slate-500"}`} data-testid={`tab-visibility-${key}`}>
              <input
                type="checkbox"
                checked={visible}
                onChange={() => toggle(key)}
                className="w-4 h-4 accent-blue-700"
              />
              {label}
            </label>
          );
        })}
      </div>
    </div>
  );
}

// ============== INVITES SECTION ==============
function InvitesSection() {
  const [invites, setInvites] = useState([]);
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);
  const [scope, setScope] = useState("own_tenant");
  const [role, setRole] = useState("employee");
  const [note, setNote] = useState("");
  const [ttl, setTtl] = useState(30);
  const [copied, setCopied] = useState(null);
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data: me } = await axios.get(`${API}/api/auth/me`);
      setIsSuperAdmin((me.tenant_id || "default") === "default" && me.role === "admin");
      const { data } = await axios.get(`${API}/api/tenant/invites`);
      setInvites(data || []);
    } catch (e) {
      console.error(e);
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const create = async () => {
    try {
      await axios.post(`${API}/api/tenant/invites`, {
        scope, role, ttl_days: ttl, note: note.trim() || null,
      });
      setCreating(false); setNote(""); setTtl(30);
      await load();
    } catch (e) {
      alert(e?.response?.data?.detail || "Nie udało się utworzyć kodu");
    }
  };

  const revoke = async (code) => {
    if (!window.confirm("Cofnąć ten kod?")) return;
    try {
      await axios.delete(`${API}/api/tenant/invites/${code}`);
      await load();
    } catch (e) {
      alert(e?.response?.data?.detail || "Błąd");
    }
  };

  const copy = async (code) => {
    try {
      await navigator.clipboard.writeText(code);
      setCopied(code);
      setTimeout(() => setCopied(null), 1500);
    } catch { /* ignore */ }
  };

  return (
    <div className="bg-white rounded-xl border border-slate-200 shadow-sm">
      <div className="flex items-center justify-between gap-2 px-4 sm:px-5 py-3 border-b border-slate-100">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-md bg-blue-50 text-blue-700 flex items-center justify-center">
            <UserPlus className="w-4 h-4" weight="fill" />
          </div>
          <div>
            <div className="text-sm font-semibold text-slate-800">Zaproszenia</div>
            <div className="text-xs text-slate-500">
              {isSuperAdmin ? "Zapraszaj nowe firmy lub pracowników do swojej firmy" : "Zapraszaj nowych pracowników do swojej firmy"}
            </div>
          </div>
        </div>
        <button
          onClick={() => { setScope(isSuperAdmin ? "new_tenant" : "own_tenant"); setRole(isSuperAdmin ? "admin" : "employee"); setCreating(true); }}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-blue-700 hover:bg-blue-800 text-white rounded-md text-xs font-medium"
          data-testid="invites-new-btn"
        >
          <Plus className="w-3.5 h-3.5" weight="bold" /> Nowy kod
        </button>
      </div>
      <div className="p-4 sm:p-5">
        {loading ? (
          <div className="text-center py-6 text-slate-400">Ładowanie...</div>
        ) : invites.length === 0 ? (
          <div className="text-sm text-slate-500 text-center py-6">Brak aktywnych kodów.</div>
        ) : (
          <div className="divide-y divide-slate-100">
            {invites.map((inv) => (
              <div key={inv.code} className="py-2.5 flex items-center gap-3" data-testid={`invite-${inv.code}`}>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <code className="font-mono font-bold text-sm text-blue-800 bg-blue-50 px-2 py-0.5 rounded">{inv.code}</code>
                    <button onClick={() => copy(inv.code)} className="text-slate-500 hover:text-blue-700" title="Skopiuj">
                      <Copy className="w-3.5 h-3.5" />
                    </button>
                    {copied === inv.code && <span className="text-xs text-emerald-600">Skopiowane</span>}
                    <span className={`text-[10px] uppercase tracking-wide font-semibold px-1.5 py-0.5 rounded ${
                      inv.scope === "new_tenant" ? "bg-purple-100 text-purple-800" : "bg-slate-100 text-slate-700"
                    }`}>
                      {inv.scope === "new_tenant" ? "Nowa firma" : "Twoja firma"}
                    </span>
                    <span className="text-[10px] uppercase font-semibold px-1.5 py-0.5 rounded bg-slate-100 text-slate-700">{inv.role}</span>
                    {!inv.active && <span className="text-[10px] uppercase font-semibold px-1.5 py-0.5 rounded bg-red-100 text-red-700">Nieaktywny</span>}
                    {inv.used_by && <span className="text-[10px] uppercase font-semibold px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-700">Użyty</span>}
                  </div>
                  <div className="text-xs text-slate-500 mt-0.5">
                    {inv.note && <span className="mr-2">{inv.note}</span>}
                    Wygasa: {new Date(inv.expires_at).toLocaleDateString("pl-PL")}
                    {inv.used_at && <> · Użyty: {new Date(inv.used_at).toLocaleDateString("pl-PL")}</>}
                  </div>
                </div>
                {inv.active && !inv.used_by && (
                  <button onClick={() => revoke(inv.code)} className="p-1.5 text-red-600 hover:bg-red-50 rounded-md" title="Cofnij" data-testid={`invite-revoke-${inv.code}`}>
                    <Trash className="w-4 h-4" />
                  </button>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {creating && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md p-6" data-testid="invites-create-modal">
            <h3 className="text-lg font-bold text-slate-800 mb-4">Nowy kod zaproszenia</h3>
            <div className="space-y-3">
              {isSuperAdmin && (
                <div>
                  <label className="text-xs font-medium text-slate-600 block mb-1">Zakres</label>
                  <select value={scope} onChange={(e) => setScope(e.target.value)} className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm bg-white" data-testid="invites-scope">
                    <option value="new_tenant">Nowa firma (super-admin)</option>
                    <option value="own_tenant">Do własnej firmy</option>
                  </select>
                </div>
              )}
              <div>
                <label className="text-xs font-medium text-slate-600 block mb-1">Rola</label>
                <select value={role} onChange={(e) => setRole(e.target.value)} className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm bg-white" data-testid="invites-role">
                  <option value="employee">Pracownik</option>
                  <option value="admin">Admin</option>
                </select>
              </div>
              <div>
                <label className="text-xs font-medium text-slate-600 block mb-1">Ważność (dni)</label>
                <input type="number" min={1} max={180} value={ttl} onChange={(e) => setTtl(parseInt(e.target.value) || 30)} className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm" data-testid="invites-ttl" />
              </div>
              <div>
                <label className="text-xs font-medium text-slate-600 block mb-1">Notatka (opcjonalna)</label>
                <input type="text" value={note} onChange={(e) => setNote(e.target.value)} placeholder="np. dla Jan Kowalski" className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm" data-testid="invites-note" />
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <button onClick={() => setCreating(false)} className="px-4 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-md text-sm">Anuluj</button>
                <button onClick={create} className="px-4 py-2 bg-blue-700 hover:bg-blue-800 text-white rounded-md text-sm font-medium" data-testid="invites-create-submit">Wygeneruj</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ============== INPUT HELPERS ==============
function TimeField({ label, value, onChange, testid }) {
  return (
    <div>
      <label className="text-xs font-medium text-slate-600 block mb-1">{label}</label>
      <input
        type="time"
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm"
        data-testid={testid}
      />
    </div>
  );
}

function TextField({ label, value, onChange, hint, testid }) {
  return (
    <div>
      <label className="text-xs font-medium text-slate-600 block mb-1">{label}</label>
      <input
        type="text"
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm"
        data-testid={testid}
      />
      {hint && <div className="text-[11px] text-slate-500 mt-0.5">{hint}</div>}
    </div>
  );
}

function NumberField({ label, value, onChange, hint, testid }) {
  return (
    <div>
      <label className="text-xs font-medium text-slate-600 block mb-1">{label}</label>
      <input
        type="number"
        min={0}
        value={value ?? 0}
        onChange={(e) => onChange(Number(e.target.value) || 0)}
        className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm"
        data-testid={testid}
      />
      {hint && <div className="text-[11px] text-slate-500 mt-0.5">{hint}</div>}
    </div>
  );
}

// ============== DYZURY REMINDERS EDITOR ==============
const REMINDER_DAYS_PL = [
  ["mon", "Poniedziałek"], ["tue", "Wtorek"], ["wed", "Środa"],
  ["thu", "Czwartek"], ["fri", "Piątek"], ["sat", "Sobota"], ["sun", "Niedziela"],
];

function PrzebiegiReminderEditor({ value, onChange }) {
  const v = value || { day: 1, time: "06:00", admin_time: "09:00", notify_admin: true, repeat_hourly: false, repeat_until_hour: 20, vehicle_scope: "all" };
  const set = (patch) => onChange({ ...v, ...patch });

  return (
    <div className="space-y-4">
      <div className="text-[11px] text-slate-500">
        Wybierz kiedy system pinguje kurierów i admina o brakującym wpisie przebiegu. Powiadomienia otrzymują tylko osoby przypisane do pojazdu — a kto już wpisał, ten NIE dostanie kolejnego pinga.
      </div>

      {/* Day + scope */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <label className="text-xs font-medium text-slate-600 block mb-1">Dzień miesiąca</label>
          <select
            value={v.day || 1}
            onChange={(e) => set({ day: parseInt(e.target.value, 10) })}
            className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm bg-white"
            data-testid="przebiegi-reminder-day"
          >
            {Array.from({ length: 28 }, (_, i) => i + 1).map((d) => (
              <option key={d} value={d}>{d}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="text-xs font-medium text-slate-600 block mb-1">Które pojazdy</label>
          <select
            value={v.vehicle_scope || "all"}
            onChange={(e) => set({ vehicle_scope: e.target.value })}
            className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm bg-white"
            data-testid="przebiegi-reminder-scope"
          >
            <option value="all">Wszystkie (firmowe i prywatne)</option>
            <option value="firmowy">Tylko firmowe</option>
            <option value="osobowy">Tylko prywatne (osobowe)</option>
          </select>
        </div>
      </div>

      {/* Employee reminder */}
      <div className="border border-slate-200 rounded p-3 bg-slate-50">
        <div className="text-xs font-semibold text-slate-700 mb-2">Powiadomienie dla kurierów</div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <TimeField
            label="Pierwsze przypomnienie"
            value={v.time || "06:00"}
            onChange={(t) => set({ time: t })}
            testid="przebiegi-reminder-time"
          />
          <div className="flex items-end">
            <label className="flex items-center gap-2 text-sm py-2 cursor-pointer">
              <input
                type="checkbox"
                checked={!!v.repeat_hourly}
                onChange={(e) => set({ repeat_hourly: e.target.checked })}
                data-testid="przebiegi-reminder-repeat"
              />
              Powtarzaj co godzinę
            </label>
          </div>
          <div>
            <label className="text-xs font-medium text-slate-600 block mb-1">Do której godziny powtarzać</label>
            <select
              value={v.repeat_until_hour ?? 20}
              onChange={(e) => set({ repeat_until_hour: parseInt(e.target.value, 10) })}
              disabled={!v.repeat_hourly}
              className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm bg-white disabled:bg-slate-100 disabled:text-slate-400"
              data-testid="przebiegi-reminder-until"
            >
              {Array.from({ length: 24 }, (_, i) => i).map((h) => (
                <option key={h} value={h}>{String(h).padStart(2, "0")}:00</option>
              ))}
            </select>
          </div>
        </div>
        <div className="text-[11px] text-slate-500 mt-2">
          Jeżeli kurier wpisze przebieg wcześniej — kolejne godzinowe pingi zostaną automatycznie pominięte.
        </div>
      </div>

      {/* Admin summary */}
      <div className="border border-slate-200 rounded p-3 bg-slate-50">
        <div className="flex items-center justify-between mb-2">
          <div className="text-xs font-semibold text-slate-700">Podsumowanie dla administratora</div>
          <label className="flex items-center gap-2 text-sm cursor-pointer">
            <input
              type="checkbox"
              checked={!!v.notify_admin}
              onChange={(e) => set({ notify_admin: e.target.checked })}
              data-testid="przebiegi-reminder-notify-admin"
            />
            Włączone
          </label>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <TimeField
            label="Godzina raportu do admina"
            value={v.admin_time || "09:00"}
            onChange={(t) => set({ admin_time: t })}
            testid="przebiegi-reminder-admin-time"
          />
          <div className="text-[11px] text-slate-500 flex items-end">
            Admin dostaje jeden push dziennie z listą wszystkich pojazdów bez wpisu.
          </div>
        </div>
      </div>
    </div>
  );
}

function DyzuryRemindersEditor({ value, onChange }) {
  const list = Array.isArray(value) ? value : [];

  const update = (i, patch) => {
    const next = list.map((r, idx) => (idx === i ? { ...r, ...patch } : r));
    onChange(next);
  };
  const remove = (i) => onChange(list.filter((_, idx) => idx !== i));
  const add = () => onChange([...list, { weekday: "fri", time: "08:00" }]);

  return (
    <div>
      <div className="text-[11px] text-slate-500 mb-3">
        Dodaj kombinacje <b>dzień + godzina</b>, kiedy pracownicy mają otrzymać push o nadchodzącym dyżurze sobotnim. Powiadomienie odnosi się zawsze do najbliższej soboty.
      </div>
      {list.length === 0 ? (
        <div className="text-sm text-slate-400 italic mb-3">Brak przypomnień — nikt nie zostanie powiadomiony.</div>
      ) : (
        <div className="space-y-2 mb-3">
          {list.map((r, i) => (
            <div key={i} className="flex items-center gap-2" data-testid={`dyzury-reminder-${i}`}>
              <select
                value={r.weekday || "fri"}
                onChange={(e) => update(i, { weekday: e.target.value })}
                className="px-3 py-2 border border-slate-300 rounded-md text-sm bg-white"
                data-testid={`dyzury-reminder-${i}-day`}
              >
                {REMINDER_DAYS_PL.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
              </select>
              <span className="text-sm text-slate-500">o</span>
              <input
                type="time"
                value={r.time || "08:00"}
                onChange={(e) => update(i, { time: e.target.value })}
                className="px-3 py-2 border border-slate-300 rounded-md text-sm"
                data-testid={`dyzury-reminder-${i}-time`}
              />
              <button
                onClick={() => remove(i)}
                className="p-2 text-red-600 hover:bg-red-50 rounded-md"
                title="Usuń"
                data-testid={`dyzury-reminder-${i}-delete`}
              >
                <Trash className="w-4 h-4" />
              </button>
            </div>
          ))}
        </div>
      )}
      <button
        onClick={add}
        className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-slate-100 hover:bg-slate-200 text-slate-800 rounded-md text-xs font-medium"
        data-testid="dyzury-reminder-add"
      >
        <Plus className="w-3.5 h-3.5" weight="bold" /> Dodaj przypomnienie
      </button>
    </div>
  );
}

function OrlenSettingsEditor() {
  const [state, setState] = useState({
    login: "", id_floty: "", enabled: false, interval_hours: 24, pull_days_back: 7, has_password: false,
  });
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(null); // "test" | "save" | "pull"
  const [flash, setFlash] = useState(null);
  const [history, setHistory] = useState([]);

  const loadAll = useCallback(async () => {
    try {
      const [s, h] = await Promise.all([
        axios.get(`${window.location.origin}/api/orlen/settings`),
        axios.get(`${window.location.origin}/api/orlen/status`),
      ]);
      setState((p) => ({ ...p, ...s.data }));
      setHistory(h.data.history || []);
    } catch (e) { /* first load may fail, ignore */ }
  }, []);
  useEffect(() => { loadAll(); }, [loadAll]);

  const notify = (kind, msg) => { setFlash({ kind, msg }); setTimeout(() => setFlash(null), 5000); };

  const testConn = async () => {
    if (!state.login || !password || !state.id_floty) {
      notify("err", "Wprowadź login, hasło i ID floty przed testem.");
      return;
    }
    setBusy("test");
    try {
      const { data } = await axios.post(`${window.location.origin}/api/orlen/test`, {
        login: state.login, password, id_floty: state.id_floty,
      });
      notify("ok", `Połączenie OK — Orlen zwrócił ${data.transactions_found} transakcji z ostatniej doby.`);
    } catch (e) {
      notify("err", e.response?.data?.detail || "Błąd testu");
    } finally { setBusy(null); }
  };

  const save = async () => {
    setBusy("save");
    try {
      const payload = { ...state };
      if (password) payload.password = password;
      else if (!state.has_password) { notify("err", "Wprowadź hasło przy pierwszym zapisie."); setBusy(null); return; }
      await axios.put(`${window.location.origin}/api/orlen/settings`, payload);
      setPassword("");
      await loadAll();
      notify("ok", "Ustawienia zapisane. Automatyczna synchronizacja aktywna.");
    } catch (e) {
      notify("err", e.response?.data?.detail || "Błąd zapisu");
    } finally { setBusy(null); }
  };

  const pullNow = async () => {
    setBusy("pull");
    try {
      const { data } = await axios.post(`${window.location.origin}/api/orlen/pull?days_back=${state.pull_days_back}`);
      if (data.ok) {
        notify("ok", `Pobrano: +${data.inserted} nowych, ${data.matched} sparowanych, ${data.unmatched} bez pary.`);
      } else {
        notify("err", `Błąd: ${data.error || "?"}`);
      }
      await loadAll();
    } catch (e) {
      notify("err", e.response?.data?.detail || "Błąd pobrania");
    } finally { setBusy(null); }
  };

  const backfillPojazd = async () => {
    if (!window.confirm("Uzupełnić pojazd_id w starych rekordach Orlen (bez pojazdu) i usunąć duplikaty? Operacja bezpieczna — tylko dopasowuje karty do pojazdów.")) return;
    setBusy("backfill");
    try {
      const { data } = await axios.post(`${window.location.origin}/api/orlen/backfill-pojazd`);
      const unmatchedTxt = (data.unmatched_cards || []).length
        ? ` · ${data.unmatched_cards.length} karta(y) bez pojazdu`
        : "";
      notify("ok", `Dopasowano: ${data.linked}, usunięto duplikatów: ${data.deleted_duplicates}${unmatchedTxt}`);
    } catch (e) {
      notify("err", e.response?.data?.detail || "Błąd backfillu");
    } finally { setBusy(null); }
  };

  const dedupeTankowania = async () => {
    if (!window.confirm("Wyszukać i usunąć duplikaty tankowań (ta sama data + karta + kwota + litry)? Zachowa rekord z przypisanym pojazdem.")) return;
    setBusy("dedupe");
    try {
      const { data } = await axios.post(`${window.location.origin}/api/orlen/dedupe`);
      notify("ok", `Usunięto ${data.deleted} duplikatów z ${data.duplicate_groups} grup.`);
    } catch (e) {
      notify("err", e.response?.data?.detail || "Błąd dedupe");
    } finally { setBusy(null); }
  };

  const backfillTenant = async () => {
    if (!window.confirm("Przypisać tenant_id do tankowań utworzonych przez wcześniejszy cron (były niewidoczne w Paliwo). Jednorazowa akcja po tej naprawie.")) return;
    setBusy("bftenant");
    try {
      const { data } = await axios.post(`${window.location.origin}/api/orlen/backfill-tenant`);
      notify("ok", `Zaktualizowano: ${data.tankowania_updated} tankowań, ${data.drafts_updated} draftów.`);
    } catch (e) {
      notify("err", e.response?.data?.detail || "Błąd");
    } finally { setBusy(null); }
  };

  return (
    <div className="space-y-4" data-testid="orlen-settings">
      {flash && (
        <div className={`text-sm px-3 py-2 rounded border ${flash.kind === "ok" ? "bg-green-50 border-green-200 text-green-800" : "bg-red-50 border-red-200 text-red-800"}`}>
          {flash.msg}
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <label className="text-xs font-medium text-slate-600 block mb-1">Login (Orlen Flota24)</label>
          <input className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm" value={state.login} onChange={(e) => setState({ ...state, login: e.target.value })} placeholder="np. f@77505133" data-testid="orlen-login" />
        </div>
        <div>
          <label className="text-xs font-medium text-slate-600 block mb-1">ID floty</label>
          <input className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm" value={state.id_floty} onChange={(e) => setState({ ...state, id_floty: e.target.value })} placeholder="np. 77505" data-testid="orlen-idfloty" />
        </div>
        <div className="sm:col-span-2">
          <label className="text-xs font-medium text-slate-600 block mb-1">
            Hasło {state.has_password && <span className="text-[10px] text-emerald-600 ml-1">✓ zapisane — puste = bez zmian</span>}
          </label>
          <input type="password" className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm" value={password} onChange={(e) => setPassword(e.target.value)} placeholder={state.has_password ? "•••••••••••" : "wpisz hasło"} data-testid="orlen-password" />
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <div>
          <label className="text-xs font-medium text-slate-600 block mb-1">Co ile godzin synchronizować</label>
          <select value={state.interval_hours} onChange={(e) => setState({ ...state, interval_hours: parseInt(e.target.value, 10) })} className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm bg-white" data-testid="orlen-interval">
            {[1, 3, 6, 12, 24, 48].map((h) => <option key={h} value={h}>co {h}h</option>)}
          </select>
        </div>
        <div>
          <label className="text-xs font-medium text-slate-600 block mb-1">Zakres pobierania (dni wstecz)</label>
          <input type="number" min={1} max={31} className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm" value={state.pull_days_back} onChange={(e) => setState({ ...state, pull_days_back: parseInt(e.target.value, 10) })} data-testid="orlen-days-back" />
        </div>
        <div className="flex items-end">
          <label className="flex items-center gap-2 text-sm py-2 cursor-pointer">
            <input type="checkbox" checked={!!state.enabled} onChange={(e) => setState({ ...state, enabled: e.target.checked })} data-testid="orlen-enabled" />
            Włącz automatyczną synchronizację
          </label>
        </div>
      </div>

      <div className="flex flex-wrap gap-2">
        <button onClick={testConn} disabled={busy === "test"} className="inline-flex items-center gap-1 border border-slate-300 hover:bg-slate-50 px-3 py-2 rounded-md text-sm" data-testid="orlen-test-btn">
          {busy === "test" ? <CircleNotch className="w-4 h-4 animate-spin" /> : <CheckCircle className="w-4 h-4" />}
          Testuj połączenie
        </button>
        <button onClick={save} disabled={busy === "save"} className="inline-flex items-center gap-1 bg-blue-600 hover:bg-blue-700 text-white px-3 py-2 rounded-md text-sm" data-testid="orlen-save-btn">
          {busy === "save" ? <CircleNotch className="w-4 h-4 animate-spin" /> : <FloppyDisk className="w-4 h-4" />}
          Zapisz
        </button>
        <button onClick={pullNow} disabled={busy === "pull" || !state.has_password} className="inline-flex items-center gap-1 border border-slate-300 hover:bg-slate-50 px-3 py-2 rounded-md text-sm" data-testid="orlen-pull-btn">
          {busy === "pull" ? <CircleNotch className="w-4 h-4 animate-spin" /> : <ArrowClockwise className="w-4 h-4" />}
          Pobierz teraz
        </button>
        <button onClick={backfillPojazd} disabled={busy === "backfill"} className="inline-flex items-center gap-1 border border-amber-300 hover:bg-amber-50 text-amber-700 px-3 py-2 rounded-md text-sm" data-testid="orlen-backfill-btn" title="Uzupełnij pojazd_id w starych rekordach Orlen">
          {busy === "backfill" ? <CircleNotch className="w-4 h-4 animate-spin" /> : <ArrowClockwise className="w-4 h-4" />}
          Napraw stare rekordy
        </button>
        <button onClick={dedupeTankowania} disabled={busy === "dedupe"} className="inline-flex items-center gap-1 border border-rose-300 hover:bg-rose-50 text-rose-700 px-3 py-2 rounded-md text-sm" data-testid="orlen-dedupe-btn" title="Usuń duplikaty tankowań (ta sama data+karta+kwota+litry)">
          {busy === "dedupe" ? <CircleNotch className="w-4 h-4 animate-spin" /> : <Trash className="w-4 h-4" />}
          Usuń duplikaty
        </button>
        <button onClick={backfillTenant} disabled={busy === "bftenant"} className="inline-flex items-center gap-1 border border-indigo-300 hover:bg-indigo-50 text-indigo-700 px-3 py-2 rounded-md text-sm" data-testid="orlen-backfill-tenant-btn" title="Napraw rekordy niewidoczne po cronie (jednorazowo)">
          {busy === "bftenant" ? <CircleNotch className="w-4 h-4 animate-spin" /> : <ArrowClockwise className="w-4 h-4" />}
          Napraw niewidoczne
        </button>
      </div>

      {history.length > 0 && (
        <div className="border border-slate-200 rounded bg-white">
          <div className="px-3 py-1.5 text-[11px] uppercase tracking-wide text-slate-500 border-b border-slate-100">Historia synchronizacji</div>
          <ul className="divide-y divide-slate-100 text-xs">
            {history.slice(0, 6).map((h, i) => (
              <li key={i} className="px-3 py-1.5 flex items-center gap-2">
                <span className={`w-1.5 h-1.5 rounded-full ${h.ok ? "bg-green-500" : "bg-red-500"}`} />
                <span className="font-mono text-slate-600">{h.started_at?.slice(0, 19).replace("T", " ")}</span>
                {h.ok ? (
                  <span className="text-slate-700">
                    +<b>{h.inserted}</b> nowych · <b>{h.matched}</b> spar. · <b>{h.unmatched}</b> bez pary
                  </span>
                ) : (
                  <span className="text-red-700">błąd: {h.error}</span>
                )}
                <span className="text-slate-400 ml-auto">{h.duration_ms} ms</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}


// ============== KSEF SETTINGS EDITOR ==============
function KsefSettingsEditor() {
  const [state, setState] = useState({
    nip: "",
    token: "",
    enabled: false,
    interval_hours: 24,
    pull_days_back: 7,
    has_token: false,
    base_url: "https://api.ksef.mf.gov.pl/v2",
  });
  const [busy, setBusy] = useState(null); // null | 'test' | 'save' | 'pull'
  const [msg, setMsg] = useState(null); // {kind:'ok'|'err', text}
  const [history, setHistory] = useState([]);
  const [counts, setCounts] = useState({ pending: 0, imported: 0, rejected: 0 });

  const notify = (kind, text) => {
    setMsg({ kind, text });
    // Info messages stay until replaced; ok/err auto-hide after 8s
    if (kind !== "info") {
      setTimeout(() => setMsg((cur) => (cur && cur.text === text ? null : cur)), 8000);
    }
  };

  const loadAll = useCallback(async () => {
    try {
      const s = await axios.get(`${window.location.origin}/api/ksef/settings`);
      setState((prev) => ({ ...prev, ...s.data, token: "" }));
    } catch { /* empty */ }
    try {
      const h = await axios.get(`${window.location.origin}/api/ksef/status`);
      setHistory(h.data.history || []);
    } catch { /* empty */ }
    try {
      const iv = await axios.get(`${window.location.origin}/api/ksef/invoices?status=all&limit=1`);
      setCounts(iv.data.counts || counts);
    } catch { /* empty */ }
  }, []);

  useEffect(() => { loadAll(); }, [loadAll]);

  const testConn = async () => {
    if (!state.nip && !state.has_token) {
      notify("err", "Najpierw zapisz NIP i token");
      return;
    }
    setBusy("test");
    notify("info", "Testowanie połączenia z KSeF... (może potrwać do 60 sekund)");
    try {
      // If a new token was typed, send it; otherwise backend falls back to the
      // Fernet-decrypted stored token from settings.
      const body = state.token ? { nip: state.nip, token: state.token } : {};
      const { data } = await axios.post(
        `${window.location.origin}/api/ksef/test`,
        body,
        { timeout: 120000 },
      );
      notify("ok", data.ok ? "Połączenie z KSeF udane ✓" : "Uwaga: nietypowa odpowiedź");
    } catch (e) {
      const msg =
        e.code === "ECONNABORTED"
          ? "Czas oczekiwania przekroczony (>120s). KSeF przetwarza uwierzytelnianie — spróbuj ponownie."
          : e.response?.data?.detail || e.message || "Test nieudany";
      notify("err", msg);
    } finally { setBusy(null); }
  };

  const save = async () => {
    setBusy("save");
    try {
      await axios.put(`${window.location.origin}/api/ksef/settings`, {
        nip: state.nip,
        token: state.token || undefined,
        enabled: state.enabled,
        interval_hours: state.interval_hours,
        pull_days_back: state.pull_days_back,
      });
      notify("ok", "Ustawienia zapisane. Cron zostanie zastosowany po restarcie backendu.");
      setState((p) => ({ ...p, token: "", has_token: true }));
      await loadAll();
    } catch (e) {
      notify("err", e.response?.data?.detail || "Błąd zapisu");
    } finally { setBusy(null); }
  };

  const pullNow = async () => {
    setBusy("pull");
    try {
      const { data } = await axios.post(`${window.location.origin}/api/ksef/pull?days_back=${state.pull_days_back}`);
      if (data.ok) {
        notify("ok", `Pobrano: +${data.inserted} nowych, pominięto ${data.skipped}. Sprawdź zakładkę KSeF.`);
      } else {
        notify("err", `Błąd: ${data.error || "?"}`);
      }
      await loadAll();
    } catch (e) {
      notify("err", e.response?.data?.detail || "Błąd pobrania");
    } finally { setBusy(null); }
  };

  return (
    <div className="space-y-4">
      {msg && (
        <div className={`text-sm px-3 py-2 rounded ${
          msg.kind === "ok" ? "bg-green-50 text-green-800 border border-green-200" :
          msg.kind === "info" ? "bg-blue-50 text-blue-800 border border-blue-200" :
          "bg-red-50 text-red-800 border border-red-200"
        }`}>
          {msg.kind === "info" && <CircleNotch className="inline w-4 h-4 mr-1 animate-spin" />}
          {msg.text}
        </div>
      )}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div>
          <label className="block text-xs font-semibold text-slate-500 uppercase mb-1">NIP firmy (nabywca)</label>
          <input
            type="text"
            inputMode="numeric"
            value={state.nip}
            onChange={(e) => setState({ ...state, nip: e.target.value })}
            placeholder="10 cyfr, bez myślników"
            className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm font-mono"
            data-testid="ksef-nip-input"
          />
        </div>
        <div>
          <label className="block text-xs font-semibold text-slate-500 uppercase mb-1">
            Token KSeF {state.has_token && <span className="text-green-600 normal-case">(zapisany ✓)</span>}
          </label>
          <input
            type="password"
            value={state.token}
            onChange={(e) => setState({ ...state, token: e.target.value })}
            placeholder={state.has_token ? "pozostaw puste, aby nie zmieniać" : "wklej token z Aplikacji Podatnika KSeF"}
            className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm font-mono"
            data-testid="ksef-token-input"
            autoComplete="off"
          />
        </div>
        <div>
          <label className="block text-xs font-semibold text-slate-500 uppercase mb-1">Interwał sync (godziny)</label>
          <input
            type="number"
            min={1}
            max={168}
            value={state.interval_hours}
            onChange={(e) => setState({ ...state, interval_hours: Math.max(1, Math.min(168, parseInt(e.target.value) || 24)) })}
            className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm"
            data-testid="ksef-interval-input"
          />
          <p className="text-[10px] text-slate-500 mt-1">Uwaga: KSeF limit 20 zapytań metadanych/h.</p>
        </div>
        <div>
          <label className="block text-xs font-semibold text-slate-500 uppercase mb-1">Zakres dni wstecz</label>
          <input
            type="number"
            min={1}
            max={99}
            value={state.pull_days_back}
            onChange={(e) => setState({ ...state, pull_days_back: Math.max(1, Math.min(99, parseInt(e.target.value) || 7)) })}
            className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm"
            data-testid="ksef-days-input"
          />
          <p className="text-[10px] text-slate-500 mt-1">Ruchome okno; nowo dostarczone faktury zostaną wychwycone.</p>
        </div>
      </div>
      <label className="flex items-center gap-2 text-sm">
        <input type="checkbox" checked={state.enabled}
          onChange={(e) => setState({ ...state, enabled: e.target.checked })}
          data-testid="ksef-enabled-cb"
        />
        <span>Włącz automatyczną synchronizację</span>
      </label>

      <div className="flex flex-wrap gap-2">
        <button onClick={save} disabled={busy === "save"} className="inline-flex items-center gap-1 bg-slate-800 hover:bg-slate-900 text-white px-3 py-2 rounded-md text-sm" data-testid="ksef-save-btn">
          {busy === "save" ? <CircleNotch className="w-4 h-4 animate-spin" /> : <FloppyDisk className="w-4 h-4" />}
          Zapisz
        </button>
        <button onClick={testConn} disabled={busy === "test" || (!state.token && !state.has_token)} className="inline-flex items-center gap-1 border border-slate-300 hover:bg-slate-50 px-3 py-2 rounded-md text-sm disabled:opacity-50" data-testid="ksef-test-btn" title="Testuj połączenie z KSeF (użyje zapisanego tokenu lub wpisanego)">
          {busy === "test" ? <CircleNotch className="w-4 h-4 animate-spin" /> : <CheckCircle className="w-4 h-4" />}
          {busy === "test" ? "Testowanie..." : "Testuj połączenie"}
        </button>
        <button onClick={pullNow} disabled={busy === "pull" || !state.has_token} className="inline-flex items-center gap-1 border border-slate-300 hover:bg-slate-50 px-3 py-2 rounded-md text-sm" data-testid="ksef-pull-btn">
          {busy === "pull" ? <CircleNotch className="w-4 h-4 animate-spin" /> : <ArrowClockwise className="w-4 h-4" />}
          Pobierz teraz
        </button>
      </div>

      {/* Counts pill */}
      <div className="flex flex-wrap gap-2 text-xs">
        <span className="px-2 py-1 rounded bg-amber-100 text-amber-800 border border-amber-200 font-semibold">Do przejrzenia: {counts.pending}</span>
        <span className="px-2 py-1 rounded bg-green-100 text-green-800 border border-green-200 font-semibold">Zaimportowane: {counts.imported}</span>
        <span className="px-2 py-1 rounded bg-slate-100 text-slate-700 border border-slate-200 font-semibold">Odrzucone: {counts.rejected}</span>
      </div>

      {history.length > 0 && (
        <div>
          <div className="text-xs font-semibold text-slate-500 uppercase mb-1">Historia synchronizacji</div>
          <ul className="text-xs space-y-1 max-h-40 overflow-auto">
            {history.map((h, i) => (
              <li key={i} className="flex items-center gap-2">
                <span className={`w-1.5 h-1.5 rounded-full ${h.ok ? "bg-green-500" : "bg-red-500"}`} />
                <span className="font-mono text-slate-600">{h.started_at?.slice(0, 19).replace("T", " ")}</span>
                {h.ok ? (
                  <span className="text-slate-700">+<b>{h.inserted}</b> nowych · pomin. <b>{h.skipped}</b></span>
                ) : (
                  <span className="text-red-700">błąd: {h.error}</span>
                )}
                <span className="text-slate-400 ml-auto">{h.duration_ms} ms</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

